import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL, auditApi } from "../../utils/api";
import { toast } from "react-toastify";
import { formatPhone } from "../../utils/formatPhone";

const MyConsumers = () => {
  const { user } = useContext(AuthContext);

  // Extract values directly from user object
  const tenant_id = user?.tenant_id;
  const role = user?.role;
  const tenant_type = user?.tenant_type;

  const [consumers, setConsumers] = useState([]);
  const [filteredConsumers, setFilteredConsumers] = useState([]);
  const [metadata, setMetadata] = useState(null); // Store metadata for customer admins
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCounselor, setSelectedCounselor] = useState("");
  const [selectedJobCoach, setSelectedJobCoach] = useState("");
  const [caseIdFilter, setCaseIdFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const apiBase = getApiBaseURL();
  const navigate = useNavigate();

  const getStatusBadgeClass = (status) => {
    if (!status) return "bg-secondary";
    const statusLower = status.toLowerCase();

    // Map status values to Bootstrap badge colors
    const statusColorMap = {
      active: "bg-success",
      inactive: "bg-secondary",
      pending: "bg-warning",
      closed: "bg-danger",
      "on hold": "bg-info",
      suspended: "bg-dark",
    };

    return statusColorMap[statusLower] || "bg-secondary";
  };

  useEffect(() => {
    const fetchConsumers = async () => {
      // Validation before making the request
      if (!user?.user_id || !tenant_id || !role || !tenant_type) {
        toast.error("Authentication error: Missing user information");
        setLoading(false);
        return;
      }

      const params = {
        user_id: user.user_id,
        tenant_id,
        role,
        tenant_type,
      };

      try {
        const res = await secureAxios.get(`${apiBase}/api/my-consumers`, {
          params,
          timeout: 10000,
        });

        // Handle both response formats
        if (res.data && typeof res.data === "object" && res.data.consumers) {
          // Customer admin response with metadata
          setConsumers(res.data.consumers || []);
          setFilteredConsumers(res.data.consumers || []);
          setMetadata(res.data.metadata || null);
        } else if (Array.isArray(res.data)) {
          // Standard array response for other roles
          setConsumers(res.data);
          setFilteredConsumers(res.data);
          setMetadata(null);
        } else {
          // Fallback
          console.warn("Unexpected API response format:", res.data);
          setConsumers([]);
          setFilteredConsumers([]);
          setMetadata(null);
        }

        // Log audit event for viewing consumer list
        const consumerCount = Array.isArray(res.data)
          ? res.data.length
          : res.data.consumers?.length || 0;
        await auditApi.logActionSafe("VIEW_CONSUMER_LIST", "CONSUMER", null, {
          consumer_count: consumerCount,
          purpose: "View assigned consumers",
          contains_phi: false,
        });
      } catch (err) {
        if (err.response) {
          const errorMessage =
            err.response.data?.error ||
            err.response.data?.details ||
            `Server error (${err.response.status})`;
          toast.error(`Failed to load consumers: ${errorMessage}`);
        } else if (err.request) {
          toast.error("Failed to load consumers: No response from server");
        } else {
          toast.error(`Failed to load consumers: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (user?.user_id) fetchConsumers();
  }, [user, tenant_id, role, tenant_type, apiBase]);

  // Advanced filtering effect
  useEffect(() => {
    let filtered = consumers;

    // Search term filter (name, email, case ID)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (consumer) =>
          consumer.name?.toLowerCase().includes(term) ||
          consumer.email?.toLowerCase().includes(term) ||
          consumer.case_id?.toLowerCase().includes(term)
      );
    }

    // Counselor filter
    if (selectedCounselor) {
      filtered = filtered.filter(
        (consumer) => consumer.counselor_name === selectedCounselor
      );
    }

    // Job Coach filter
    if (selectedJobCoach) {
      filtered = filtered.filter(
        (consumer) => consumer.job_coach_name === selectedJobCoach
      );
    }

    // Case ID specific filter
    if (caseIdFilter) {
      const caseId = caseIdFilter.toLowerCase();
      filtered = filtered.filter((consumer) =>
        consumer.case_id?.toLowerCase().includes(caseId)
      );
    }

    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter(
        (consumer) =>
          consumer.status_name?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Assignment filter (has job coach or not)
    if (selectedAssignment) {
      if (selectedAssignment === "assigned") {
        filtered = filtered.filter((consumer) => consumer.job_coach_name);
      } else if (selectedAssignment === "unassigned") {
        filtered = filtered.filter((consumer) => !consumer.job_coach_name);
      }
    }

    setFilteredConsumers(filtered);
  }, [
    consumers,
    searchTerm,
    selectedCounselor,
    selectedJobCoach,
    caseIdFilter,
    selectedStatus,
    selectedAssignment,
  ]);

  // Get unique values for filter dropdowns
  const uniqueCounselors = [
    ...new Set(consumers.map((c) => c.counselor_name).filter(Boolean)),
  ].sort();

  const uniqueJobCoaches = [
    ...new Set(consumers.map((c) => c.job_coach_name).filter(Boolean)),
  ].sort();

  const uniqueStatuses = [
    ...new Set(consumers.map((c) => c.status_name).filter(Boolean)),
  ].sort();

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCounselor("");
    setSelectedJobCoach("");
    setCaseIdFilter("");
    setSelectedStatus("");
    setSelectedAssignment("");
  };

  const handleViewInfo = async (consumer) => {
    // Log viewing consumer info
    await auditApi.logActionSafe(
      "VIEW_CONSUMER_INFO",
      "CONSUMER",
      consumer.consumer_id,
      {
        consumer_name: consumer.name,
        counselor: consumer.counselor_name,
        contains_phi: true,
      }
    );
    navigate(`/crm/my-consumers/${consumer.consumer_id}/info`);
  };

  const handleViewAuths = async (consumer) => {
    // Log viewing authorizations
    await auditApi.logActionSafe(
      "VIEW_CONSUMER_AUTHORIZATIONS",
      "CONSUMER",
      consumer.consumer_id,
      {
        consumer_name: consumer.name,
        counselor: consumer.counselor_name,
        contains_phi: true,
      }
    );
    navigate(`/crm/my-consumers/${consumer.consumer_id}/auths`);
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading consumers...</span>
        </div>
      </div>
    );

  // Display helper text based on role
  const getRoleDescription = () => {
    const roleType = `${tenant_type}-${role}`.toLowerCase();
    switch (roleType) {
      case "customer-admin":
        return "Showing all consumers linked to counselors in your organization";
      case "customer-counselor":
        return "Showing consumers assigned to you";
      case "vendor-admin":
        return "Showing all consumers where counselors are linked to your vendor";
      case "vendor-job coach":
        return "Showing consumers you are assigned to work with";
      default:
        return "Showing your assigned consumers";
    }
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    const filters = [
      searchTerm,
      selectedCounselor,
      selectedJobCoach,
      caseIdFilter,
      selectedStatus,
      selectedAssignment,
    ];
    return filters.filter(Boolean).length;
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>My Consumers</h2>
          <p className="text-muted mb-0">{getRoleDescription()}</p>

          {/* Show metadata for customer admins */}
          {metadata && (
            <div className="mt-2">
              <small className="text-info">
                <i className="bi bi-info-circle me-1"></i>
                {metadata.total_consumers} consumers across{" "}
                {metadata.unique_counselors} counselors
                {metadata.unique_job_coaches > 0 && (
                  <span>, {metadata.unique_job_coaches} job coaches</span>
                )}
                {metadata.access_level && (
                  <span className="badge bg-info ms-2">
                    {metadata.access_level.replace(/_/g, " ")}
                  </span>
                )}
              </small>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="bi bi-funnel me-1"></i>
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="badge bg-primary ms-1">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
          <div className="text-muted small">
            <i className="bi bi-shield-check text-success me-1"></i>
            PHI Access Authorized
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="card mb-3">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="bi bi-funnel me-2"></i>
                Advanced Filters
              </h6>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={clearFilters}
                disabled={getActiveFilterCount() === 0}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear All
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* First Row of Filters */}
            <div className="row g-3">
              {/* Search Term */}
              <div className="col-md-3">
                <label className="form-label">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name, email, or case ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Case ID Filter */}
              <div className="col-md-2">
                <label className="form-label">Case ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by case ID..."
                  value={caseIdFilter}
                  onChange={(e) => setCaseIdFilter(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <div className="col-md-2">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {uniqueStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Counselor Filter */}
              <div className="col-md-2">
                <label className="form-label">Counselor</label>
                <select
                  className="form-select"
                  value={selectedCounselor}
                  onChange={(e) => setSelectedCounselor(e.target.value)}
                >
                  <option value="">All Counselors</option>
                  {uniqueCounselors.map((counselor) => (
                    <option key={counselor} value={counselor}>
                      {counselor}
                    </option>
                  ))}
                </select>
              </div>

              {/* Job Coach Filter */}
              <div className="col-md-3">
                <label className="form-label">Job Coach</label>
                <select
                  className="form-select"
                  value={selectedJobCoach}
                  onChange={(e) => setSelectedJobCoach(e.target.value)}
                >
                  <option value="">All Job Coaches</option>
                  {uniqueJobCoaches.map((jobCoach) => (
                    <option key={jobCoach} value={jobCoach}>
                      {jobCoach}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Second Row of Filters */}
            <div className="row g-3 mt-2">
              {/* Assignment Status Filter */}
              <div className="col-md-3">
                <label className="form-label">Job Coach Assignment</label>
                <select
                  className="form-select"
                  value={selectedAssignment}
                  onChange={(e) => setSelectedAssignment(e.target.value)}
                >
                  <option value="">All Consumers</option>
                  <option value="assigned">Assigned to Job Coach</option>
                  <option value="unassigned">Unassigned (No Job Coach)</option>
                </select>
              </div>
            </div>

            {/* Results Summary */}
            <div className="mt-3 pt-3 border-top">
              <small className="text-muted">
                Showing {filteredConsumers.length} of {consumers.length}{" "}
                consumers
                {filteredConsumers.length !== consumers.length && (
                  <span className="text-info ms-2">
                    ({consumers.length - filteredConsumers.length} filtered out)
                  </span>
                )}
              </small>
            </div>
          </div>
        </div>
      )}

      {filteredConsumers.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          {consumers.length === 0
            ? "No consumers found. Consumers will appear here once they are assigned to you."
            : "No consumers match the current filters. Try adjusting your search criteria."}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Consumer Name</th>
                  <th>Consumer Phone</th>
                  <th>Case ID</th>
                  <th>Status</th>
                  {tenant_type === "vendor" ? (
                    <th>Counselor / Customer</th>
                  ) : (
                    <th>Job Coach</th>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredConsumers.map((consumer) => (
                  <tr key={consumer.consumer_id}>
                    <td>
                      <strong>{consumer.name}</strong>
                      {tenant_type === "vendor" && consumer.job_coach_name && (
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.8rem" }}
                        >
                          <i className="bi bi-person-badge me-1"></i>
                          {consumer.job_coach_name}
                        </div>
                      )}
                    </td>
                    <td>{formatPhone(consumer.phone_number) || "-"}</td>
                    <td>{consumer.case_id || "-"}</td>
                    <td>
                      <span
                        className={`badge ${getStatusBadgeClass(
                          consumer.status_name
                        )}`}
                      >
                        {consumer.status_name || "Unknown"}
                      </span>
                    </td>
                    {tenant_type === "vendor" ? (
                      <td>
                        <strong>{consumer.counselor_name || "-"}</strong>
                        {consumer.counselor_email && (
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            <i className="bi bi-envelope me-1"></i>
                            {consumer.counselor_email}
                          </div>
                        )}
                        {consumer.counselor_phone && (
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            <i className="bi bi-telephone me-1"></i>
                            {formatPhone(consumer.counselor_phone)}
                          </div>
                        )}
                        {consumer.customer_name && (
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            <i className="bi bi-building me-1"></i>
                            {consumer.customer_name}
                          </div>
                        )}
                      </td>
                    ) : (
                      <td>
                        <strong>{consumer.job_coach_name || "-"}</strong>
                        {consumer.job_coach_email && (
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            <i className="bi bi-envelope me-1"></i>
                            {consumer.job_coach_email}
                          </div>
                        )}
                        {consumer.job_coach_phone && (
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            <i className="bi bi-telephone me-1"></i>
                            {formatPhone(consumer.job_coach_phone)}
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleViewInfo(consumer)}
                          title="View Details"
                        >
                          <i className="bi bi-person-lines-fill"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleViewAuths(consumer)}
                          title="View Authorizations"
                        >
                          <i className="bi bi-file-text"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-md-none">
            {filteredConsumers.map((consumer) => (
              <div key={consumer.consumer_id} className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title mb-0">{consumer.name}</h5>
                  {tenant_type === "vendor" && consumer.job_coach_name && (
                    <div
                      className="text-muted mb-2"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <i className="bi bi-person-badge me-1"></i>
                      {consumer.job_coach_name}
                    </div>
                  )}
                  <span
                    className={`badge ${getStatusBadgeClass(
                      consumer.status_name
                    )}`}
                  >
                    {consumer.status_name || "Unknown"}
                  </span>
                  <div className="mt-2">
                    <p className="card-text mb-1">
                      <strong>Phone:</strong>{" "}
                      {formatPhone(consumer.phone_number) || "-"}
                    </p>
                    <p className="card-text mb-1">
                      <strong>Case ID:</strong> {consumer.case_id || "-"}
                    </p>
                    {tenant_type === "vendor" ? (
                      <>
                        <hr className="my-2" />
                        <p className="card-text mb-1">
                          <strong>Counselor:</strong>{" "}
                          {consumer.counselor_name || "-"}
                        </p>
                        {consumer.counselor_email && (
                          <p className="card-text mb-1" style={{ fontSize: "0.9rem" }}>
                            <i className="bi bi-envelope me-1"></i>
                            {consumer.counselor_email}
                          </p>
                        )}
                        {consumer.counselor_phone && (
                          <p className="card-text mb-1" style={{ fontSize: "0.9rem" }}>
                            <i className="bi bi-telephone me-1"></i>
                            {formatPhone(consumer.counselor_phone)}
                          </p>
                        )}
                        {consumer.customer_name && (
                          <p className="card-text mb-1" style={{ fontSize: "0.9rem" }}>
                            <i className="bi bi-building me-1"></i>
                            {consumer.customer_name}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <hr className="my-2" />
                        <p className="card-text mb-1">
                          <strong>Job Coach:</strong>{" "}
                          {consumer.job_coach_name || "-"}
                        </p>
                        {consumer.job_coach_email && (
                          <p className="card-text mb-1" style={{ fontSize: "0.9rem" }}>
                            <i className="bi bi-envelope me-1"></i>
                            {consumer.job_coach_email}
                          </p>
                        )}
                        {consumer.job_coach_phone && (
                          <p className="card-text mb-1" style={{ fontSize: "0.9rem" }}>
                            <i className="bi bi-telephone me-1"></i>
                            {formatPhone(consumer.job_coach_phone)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="mt-2">
                    <button
                      className="btn btn-primary btn-sm me-2"
                      onClick={() => handleViewInfo(consumer)}
                    >
                      <i className="bi bi-person-lines-fill me-1"></i>
                      Details
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleViewAuths(consumer)}
                    >
                      <i className="bi bi-file-text me-1"></i>
                      Authorizations
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MyConsumers;